/**
 * Demo page content — 100% fidelity to content/company/demo.md
 * Cross-reference: content/company/demo.md
 * 18 sections, all handcrafted from source.
 */

export const demoMeta = {
  title: "Request Demo | Perception X2 See It in Action",
  description:
    "Request a personalized demo of Perception X2. See how AI-powered perception management transforms brand narratives. Confidential. No obligation.",
  slug: "demo",
  canonical: "https://perception.ac/demo",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  trustIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Request a Live Demo",
  title: "See Perception X2 Operate. Not Slides. Not Abstractions.",
  titleAccent: "The Live Platform.",
  lede: "Perception X2 is the world's most advanced autonomous perception amplification platform — and this is where you witness it in action.",
  body: "Request a guided demo and observe how nine proprietary AI engines coordinate across 50+ digital platforms to deliver 300-500% improvement in narrative penetration with 95-98% organic authenticity. Every feature demonstrated on the live platform. Every capability proven in real time.",
  cta: [{ label: "Request a Live Demo", href: "#demo-form" }],
  trustIndicators: [
    "15+ Years of Operational Excellence",
    "300+ Elite Global Clients",
    "99.9999% Uptime · Zero Security Incidents",
  ],
};

export interface ExecSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const executiveSummary: ExecSummaryData = {
  eyebrow: "Executive Summary",
  title: "The Platform Behind the World's Most Successful Perception Strategies",
  intro: "Perception X2 is the autonomous perception amplification platform that transforms how organizations shape, protect, and amplify their digital presence. Built on nine proprietary AI engines and refined through 15+ years of operational excellence, it delivers verified results across the world's most demanding environments.",
  pillars: [
    {
      title: "Why a Demo Matters",
      body: "Perception X2 is not a conventional reputation management tool. It is a computational platform that operates across 50+ digital platforms, processes millions of data points in real time, and deploys coordinated narratives at machine speed. The difference between Perception X2 and conventional approaches is not incremental — it is structural. You need to see it to understand the distinction.",
    },
    {
      title: "What You Will Experience",
      body: "In a guided demo, our team walks you through the platform's core capabilities — narrative engineering, content intelligence, amplification orchestration, real-time analytics, and crisis response systems. You will observe how the platform processes intelligence, develops strategies, generates authentic content, and deploys it across platforms with 95-98% organic authenticity rating.",
    },
    {
      title: "Who Should Attend",
      body: "The demo is designed for decision-makers — C-suite executives, communications directors, reputation strategists, and technology leaders evaluating perception management at scale. Every demo is customized to your industry, use case, and strategic objectives.",
    },
  ],
  cta: [
    { label: "Schedule Your Demo", href: "#demo-form" },
    { label: "Explore Platform Features", href: "#platform-preview" },
  ],
};

export interface ProblemData {
  eyebrow: string;
  title: string;
  pillars: { title: string; body: string }[];
  intro?: string;
}

export const problem: ProblemData = {
  eyebrow: "The Problem",
  title: "Why Traditional Perception Management Cannot Keep Pace",
  intro: "",
  pillars: [
    { title: "Fragmented Monitoring", body: "Legacy platforms monitor a handful of channels and report what happened yesterday. By the time you receive the report, the narrative has moved on, competitors have amplified their position, and your team is responding to history rather than shaping the future." },
    { title: "Manual Content Workflows", body: "Traditional content creation is slow, expensive, and unscalable. A single campaign requires teams of writers, designers, and strategists working for weeks. By the time content deploys, the window of relevance has closed." },
    { title: "No Amplification Mechanism", body: "Creating content is only half the equation. Without coordinated amplification across platforms, even the best content fails to reach critical mass. Conventional PR relies on media relationships and luck. Perception X2 relies on computation." },
    { title: "Inability to Measure Impact", body: "Most organizations cannot quantify the impact of their perception efforts. Vanity metrics like impressions and likes reveal nothing about actual narrative penetration or audience sentiment. Without measurement, improvement is impossible." },
    { title: "Crisis Vulnerability", body: "Organizations face reputation threats that escalate in minutes, not days. By the time a traditional response team mobilizes, the crisis has already spread across platforms, shaped public opinion, and inflicted lasting damage. The cost of slow response is measured in brand equity lost." },
  ],
};

export interface SolutionData {
  eyebrow: string;
  title: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  intro?: string;
}

export const solution: SolutionData = {
  eyebrow: "The Solution",
  title: "How Perception X2 Transforms Perception Management",
  intro: "",
  pillars: [
    { title: "Autonomous Intelligence", body: "Nine proprietary AI engines continuously analyze 50+ digital platforms, processing millions of data points in real time. Machine learning models trained on billions of interactions predict narrative trajectories before they materialize. You do not react to threats — you anticipate and neutralize them." },
    { title: "Narrative Engineering at Scale", body: "Perception X2's Narrative Generation Engine creates authentic, platform-optimized content across 50+ templates. Every piece is engineered for maximum resonance while maintaining 95-98% organic authenticity — content statistically indistinguishable from organic engagement." },
    { title: "Coordinated Amplification", body: "The Organic Simulation Engine deploys coordinated narratives across 10,000 to 1,000,000+ personas, achieving 300-500% improvement in narrative penetration. Every deployment is optimized for platform-specific algorithms and audience behaviors." },
    { title: "Real-Time Analytics", body: "144-dimensional emotional resonance mapping provides unprecedented visibility into audience sentiment and narrative performance. Track perception shifts across geographies, demographics, and platforms simultaneously. Understand not just what people say — understand why." },
  ],
  cta: [
    { label: "See the Solution in Action", href: "#platform-preview" },
    { label: "Request a Demo", href: "#demo-form" },
  ],
};

export interface WhatDemoCoversData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const whatDemoCovers: WhatDemoCoversData = {
  eyebrow: "What Your Demo Covers",
  title: "A Complete Platform Experience, Tailored to Your Objectives",
  intro: "Your Perception X2 demo is not a slide deck. It is a live, guided walkthrough of the actual platform — demonstrating capabilities that conventional reputation management tools cannot replicate.",
  pillars: [
    {
      title: "Narrative Engineering in Action",
      body: "Watch the Narrative Generation Engine develop, test, and optimize narratives in real time. Observe how the platform constructs story frameworks, applies 144-dimensional emotional resonance mapping, and produces content engineered for maximum audience impact across specific platforms.",
    },
    {
      title: "Intelligence Processing at Scale",
      body: "See how the platform ingests and processes millions of data points across 50+ digital platforms simultaneously. Examine the raw intelligence pipeline that powers every strategic decision — from sentiment analysis to competitive positioning to threat detection.",
    },
    {
      title: "Amplification Orchestration",
      body: "Witness the Organic Simulation Engine coordinate deployment across platform-specific algorithms. Watch as the platform deploys coordinated narratives across 10,000-1,000,000+ personas, achieving 95-98% organic authenticity rating at every scale.",
    },
    {
      title: "Real-Time Analytics Dashboard",
      body: "Experience 144-dimensional emotional resonance tracking across geographies, demographics, and platforms. See how narrative penetration, audience sentiment, share of voice, and competitive positioning are measured and displayed in real time.",
    },
    {
      title: "Crisis Response Activation",
      body: "Observe the platform's crisis detection, assessment, and counter-narrative deployment capabilities. See how threats are identified within minutes and neutralized in hours — not days.",
    },
  ],
  cta: [
    { label: "Schedule Your Demo", href: "#demo-form" },
    { label: "Explore Each Feature", href: "#platform-preview" },
  ],
};

export interface DemoFormData {
  eyebrow: string;
  title: string;
  intro: string;
  fields: { field: string; type: string; required: string }[];
  roleOptions: string[];
  industryOptions: string[];
  sizeOptions: string[];
  interestOptions: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const demoForm: DemoFormData = {
  eyebrow: "Demo Request Form",
  title: "Request Your Perception X2 Demo",
  intro: "Complete the form below and our team will respond within 24 hours to schedule your personalized demo.",
  fields: [
    { field: "Full Name", type: "Text", required: "Yes" },
    { field: "Email Address", type: "Email", required: "Yes" },
    { field: "Phone Number", type: "Phone", required: "No" },
    { field: "Organization", type: "Text", required: "Yes" },
    { field: "Job Title", type: "Text", required: "Yes" },
    { field: "Role", type: "Dropdown", required: "Yes" },
    { field: "Industry", type: "Dropdown", required: "Yes" },
    { field: "Organization Size", type: "Dropdown", required: "No" },
    { field: "Primary Interest", type: "Dropdown", required: "Yes" },
    { field: "Current Challenges", type: "Textarea", required: "No" },
    { field: "Preferred Date", type: "Date", required: "No" },
    { field: "NDA Required", type: "Checkbox", required: "No" },
  ],
  roleOptions: [
    "C-Suite / Executive Leadership",
    "VP / Senior Vice President",
    "Director",
    "Manager",
    "Consultant / Advisor",
    "Other",
  ],
  industryOptions: [
    "Government & Sovereign Entities",
    "Enterprise & Corporate",
    "Political Campaigns & Organizations",
    "Defense & Intelligence",
    "Financial Services",
    "Healthcare",
    "Technology",
    "Media & Entertainment",
    "Public Figure / HNWI",
    "Other",
  ],
  sizeOptions: [
    "1-50 employees",
    "51-200 employees",
    "201-1,000 employees",
    "1,001-5,000 employees",
    "5,001-10,000 employees",
    "10,000+ employees",
  ],
  interestOptions: [
    "Reputation Management",
    "Crisis Response & Prevention",
    "Narrative Engineering & Amplification",
    "Competitive Intelligence",
    "Brand Perception Architecture",
    "Executive / Personal Brand Management",
    "Political Campaign Perception",
    "Comprehensive Platform Overview",
  ],
  cta: [{ label: "Submit Demo Request", href: "#demo-form" }],
};

export interface WhatToExpectData {
  eyebrow: string;
  title: string;
  intro: string;
  steps: { step: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const whatToExpect: WhatToExpectData = {
  eyebrow: "What to Expect",
  title: "Your Demo Process, From Request to Results",
  intro: "Every Perception X2 demo follows a structured process designed to deliver maximum value in minimum time. Here is what happens after you submit your request.",
  steps: [
    { step: "Step 1", title: "Request Received", body: "Your demo request is reviewed by our team within 24 hours. We assess your organization, industry, and stated objectives to match you with the appropriate specialist." },
    { step: "Step 2", title: "Pre-Demo Consultation", body: "Before your demo, our team conducts a brief consultation — typically 15-20 minutes — to understand your specific challenges, competitive landscape, and strategic goals. This ensures the demo showcases capabilities most relevant to your needs." },
    { step: "Step 3", title: "Guided Platform Demo", body: "Your demo is conducted by a senior Perception X2 specialist who walks you through the live platform. Every feature is demonstrated in the context of your specific objectives. The standard session runs 45-60 minutes." },
    { step: "Step 4", title: "Q&A and Strategic Discussion", body: "After the demonstration, our team answers your questions and discusses how Perception X2 applies to your organization. We provide a customized assessment based on your demonstration experience." },
    { step: "Step 5", title: "Customized Proposal", body: "Based on your demo and consultation, we deliver a tailored proposal outlining the recommended deployment tier, scope, and timeline for your organization. No obligation. No pressure." },
  ],
  cta: [{ label: "Begin Step 1", href: "#demo-form" }],
};

export interface WalkthroughData {
  eyebrow: string;
  title: string;
  intro: string;
  phases: { phase: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const walkthrough: WalkthroughData = {
  eyebrow: "Demo Walkthrough",
  title: "From Intelligence to Outcomes: The Perception X2 Process",
  intro: "Your demo follows the six-phase methodology that drives every Perception X2 engagement. Each phase demonstrates a distinct platform capability.",
  phases: [
    { phase: "Phase 1", title: "Intelligence Gathering", body: "The platform scans 50+ digital platforms, analyzing millions of data points to map your current perception landscape. You will see how the system identifies opportunities, threats, and whitespace that manual analysis would miss." },
    { phase: "Phase 2", title: "Analysis and Pattern Recognition", body: "Machine learning algorithms process raw intelligence into actionable insights. The Cognitive Resonance Engine maps audience sentiment across 144 emotional dimensions. You will observe how the platform identifies what resonates and what does not." },
    { phase: "Phase 3", title: "Strategy Development", body: "AI-optimized narrative strategies are generated from intelligence insights. The platform develops content calendars, identifies optimal deployment windows, and selects platform-specific approaches. Every strategy is data-driven, not opinion-driven." },
    { phase: "Phase 4", title: "Content Creation", body: "The Narrative Generation Engine produces authentic, platform-optimized content across text, image, and video formats. You will see how 50+ templates ensure variety while maintaining message consistency and 95-98% organic authenticity." },
    { phase: "Phase 5", title: "Amplification and Deployment", body: "The Organic Simulation Engine coordinates deployment across orchestrated networks. You will observe how narratives are deployed, optimized, and scaled across platforms — achieving 300-500% improvement in narrative penetration." },
    { phase: "Phase 6", title: "Monitoring and Optimization", body: "Real-time analytics track narrative performance, sentiment shifts, and competitive positioning. You will see how continuous learning algorithms refine strategies based on performance data, ensuring perpetual improvement." },
  ],
  cta: [
    { label: "See Each Phase Live", href: "#demo-form" },
    { label: "Explore the Platform", href: "#platform-preview" },
  ],
};

export interface KeyMetricsData {
  eyebrow: string;
  title: string;
  intro: string;
  metrics: { value: string; label: string; detail: string }[];
  pillars?: { title: string; body: string }[];
}

export const keyMetrics: KeyMetricsData = {
  eyebrow: "Key Metrics",
  title: "Performance Numbers That Define the Platform",
  intro: "Every metric demonstrated in your demo is drawn from verified, repeatable outcomes across 300+ engagements.",
  metrics: [
    { value: "300-500%", label: "Narrative Penetration Improvement", detail: "Consistently achieved across diverse industries and deployment scales." },
    { value: "95-98%", label: "Organic Authenticity Rating", detail: "Content statistically indistinguishable from organic engagement." },
    { value: "50+", label: "Digital Platforms Covered", detail: "Comprehensive ecosystem coverage with zero blind spots." },
    { value: "144", label: "Emotional Resonance Dimensions", detail: "Unprecedented analytical granularity for precision targeting." },
    { value: "1,000,000+", label: "Maximum Persona Deployment", detail: "Full-spectrum operations at sovereign scale." },
    { value: "99.9999%", label: "Platform Uptime", detail: "31.5 seconds maximum downtime per year." },
  ],
};

export interface PlatformPreviewData {
  eyebrow: string;
  title: string;
  intro: string;
  capabilities: { name: string; body: string; icon: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const platformPreview: PlatformPreviewData = {
  eyebrow: "Platform Capabilities Preview",
  title: "Core Capabilities Demonstrated in Your Walkthrough",
  intro: "Every feature demonstrated in your demo uses real platform functionality — not mockups or abstractions. Here is a detailed look at what you will experience.",
  capabilities: [
    { name: "Narrative Engineering Suite", body: "The Narrative Generation Engine creates authentic, platform-optimized content across 50+ templates. You will observe how the platform analyzes successful narrative patterns across industries, adapts them for your specific context, and generates content that maintains 95-98% organic authenticity rating.", icon: "BookOpen" },
    { name: "Content Intelligence Dashboard", body: "The platform's AI processes millions of data points to identify narrative opportunities, threat vectors, audience sentiment patterns, and competitive positioning. You will see how the system surfaces actionable intelligence from the noise of 50+ digital platforms.", icon: "BarChart3" },
    { name: "Amplification Orchestration Console", body: "The Organic Simulation Engine coordinates deployment across platform-specific algorithms and audience behaviors. You will observe how narratives are deployed, A/B tested, and optimized in real time — achieving 300-500% improvement in narrative penetration.", icon: "Send" },
    { name: "144-Dimensional Analytics", body: "Experience the Cognitive Resonance Engine's analytics in action. Track narrative performance across 144 emotional dimensions, monitor sentiment shifts across geographies, and measure competitive positioning with granularity impossible in conventional platforms.", icon: "Activity" },
    { name: "Crisis Response Command Center", body: "Observe the integrated crisis detection and response system. From threat identification through counter-narrative deployment to sentiment recovery tracking — the full crisis lifecycle demonstrated in a live environment.", icon: "Siren" },
    { name: "Multi-Platform Deployment Interface", body: "Explore how Perception X2 manages simultaneous deployment across 50+ platforms — social media, search engines, news outlets, forums, review sites, podcasts, video platforms, and emerging digital surfaces — with platform-specific optimization at each surface.", icon: "Globe" },
  ],
  cta: [
    { label: "Schedule Your Demo", href: "#demo-form" },
    { label: "See Each Phase Live", href: "#walkthrough" },
  ],
};

export interface WhoAttendData {
  eyebrow: string;
  title: string;
  intro: string;
  audience: { role: string; body: string; icon: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const whoAttend: WhoAttendData = {
  eyebrow: "Who Should Attend",
  title: "Designed for Decision-Makers Who Shape Perception",
  intro: "",
  audience: [
    { role: "C-Suite Executives", body: "Leaders responsible for organizational reputation and strategic positioning. Perception X2 provides the computational advantage that transforms reputation from a liability into an asset.", icon: "User" },
    { role: "Communications Directors", body: "Professionals managing corporate communications, public relations, and stakeholder messaging. Perception X2 amplifies your reach while reducing manual workload.", icon: "Megaphone" },
    { role: "Reputation Strategists", body: "Specialists focused on brand perception, crisis prevention, and narrative engineering. Perception X2 provides the tools and intelligence to execute at scale.", icon: "Telescope" },
    { role: "Political Campaign Managers", body: "Organizers responsible for candidate perception, voter sentiment, and public discourse. Perception X2 delivers narrative dominance across demographics and platforms.", icon: "Vote" },
    { role: "Technology Leaders", body: "CTOs and CIOs evaluating AI-powered platforms for enterprise deployment. Perception X2 demonstrates scalable, secure, and integrated perception management technology.", icon: "Cpu" },
    { role: "Defense and Intelligence Professionals", body: "Organizations requiring secure, sovereign perception management capabilities. Perception X2 operates with military-grade security and zero third-party dependencies.", icon: "Shield" },
  ],
  cta: [{ label: "Is Perception X2 Right for You?", href: "#demo-form" }],
};

export interface ConfData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const confidentiality: ConfData = {
  eyebrow: "Confidentiality and Security",
  title: "Your Information Is Protected at Every Stage",
  intro: "We understand the sensitivity of evaluating perception management capabilities. Every interaction with CryptoMize is protected by the same security standards used by defense and intelligence agencies.",
  pillars: [
    { title: "NDA Availability", body: "We provide mutual non-disclosure agreements before any detailed discussion. Your organizational details, challenges, and strategic objectives remain completely confidential." },
    { title: "Encrypted Communications", body: "All demo sessions, form submissions, and communications are protected by end-to-end AES-256 encryption and TLS 1.3 transport security. Your data never traverses unencrypted channels." },
    { title: "No Information Sharing", body: "Your demo request, consultation notes, and organizational details are never shared with third parties. We operate with the same compartmentalization standards used in classified environments." },
    { title: "Secure Platform Environment", body: "The demo environment uses the same security infrastructure as our production platform — zero trust architecture, SOC 2 Type II compliance, and ISO 27001 certification." },
    { title: "15+ Years, Zero Security Incidents", body: "Our track record speaks for itself. In 15+ years of global operations, we have maintained zero security breaches. Your data is protected by the most rigorous standards in the industry." },
  ],
  cta: [
    { label: "Review Our Security Posture", href: "/about#technology" },
    { label: "Request an NDA", href: "#demo-form" },
  ],
};

export interface WhyUsData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const whyUs: WhyUsData = {
  eyebrow: "Why Choose Us",
  title: "The Perception X2 Difference",
  intro: "",
  pillars: [
    { title: "Experience That Cannot Be Replicated", body: "15+ years of operational excellence across multiple continents. We have navigated every crisis, adapted to every shift, and delivered consistently. Experience is not purchased — it is earned through results." },
    { title: "Technology That Cannot Be Copied", body: "Nine proprietary AI platforms developed in-house. Not licensed, not white-labeled — built, tested, and proven. The integration between engines creates compound advantage that competitors cannot replicate." },
    { title: "Results That Speak for Themselves", body: "300-500% narrative penetration improvement. 95-98% organic authenticity rating. 300+ elite global clients. Verified results across the world's most demanding environments." },
    { title: "Security Without Compromise", body: "Zero security incidents in 15+ years. End-to-end encryption. Sovereign infrastructure. Your data and operations are protected by the same standards used by defense and intelligence agencies." },
    { title: "Scale Without Limits", body: "From 100 personas to 1,000,000+. Four deployment tiers ensure Perception X2 scales to your exact requirements — no organization is too small, no operation is too large." },
    { title: "Commitment to Outcomes", body: "24/7/365 support. Dedicated account management. Real-time monitoring. Regular performance reviews. Your success is our reputation." },
  ],
  cta: [{ label: "Choose Perception X2", href: "#demo-form" }],
};

export interface HowWorksData {
  eyebrow: string;
  title: string;
  intro: string;
  phases: { phase: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const howItWorks: HowWorksData = {
  eyebrow: "How It Works",
  title: "The Perception X2 Methodology",
  intro: "Every engagement follows a six-phase methodology that ensures systematic, measurable perception transformation.",
  phases: [
    { phase: "Phase 1", title: "Intelligence", body: "Comprehensive data gathering and analysis across 50+ digital surfaces. We map your current perception landscape, identify competitive positioning, and uncover opportunities." },
    { phase: "Phase 2", title: "Strategy", body: "AI-optimized narrative strategies developed from intelligence insights. Every strategy is tailored to your specific objectives, audience, and competitive environment." },
    { phase: "Phase 3", title: "Execution", body: "Coordinated, multi-platform deployment with real-time optimization. Content is generated, tested, and deployed across 50+ platforms simultaneously." },
    { phase: "Phase 4", title: "Amplification", body: "Orchestrated network activation ensures your narrative reaches critical mass. 10,000-1,000,000+ personas deploy your message with organic authenticity." },
    { phase: "Phase 5", title: "Monitoring", body: "144-dimensional emotional resonance tracking provides real-time visibility into perception shifts and audience response. Track, measure, adapt." },
    { phase: "Phase 6", title: "Optimization", body: "Continuous learning algorithms refine strategies based on performance data. Every cycle builds on the last. Every campaign improves the system." },
  ],
  cta: [{ label: "See the Methodology in Action", href: "#demo-form" }],
};

export interface FaqData {
  eyebrow: string;
  title: string;
  items: { q: string; a: string }[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const demoFaq: FaqData = {
  eyebrow: "FAQ",
  title: "Frequently Asked Questions",
  items: [
    { q: "What does the Perception X2 demo include?", a: "Your demo showcases the live Perception X2 platform — not slides or mockups. You will observe narrative engineering, content intelligence, amplification orchestration, real-time analytics, and crisis response capabilities demonstrated in real time. Every feature is shown on the actual production platform." },
    { q: "How long does the demo take?", a: "A standard demo runs 45-60 minutes. Extended demos with deep-dive sessions on specific capabilities are available upon request. We accommodate scheduling across global time zones." },
    { q: "Is the demo confidential?", a: "Absolutely. All demo content, discussions, and organizational details are treated with the highest level of confidentiality. We provide mutual NDAs before any detailed discussion. We operate with the same security standards used by defense and intelligence agencies." },
    { q: "Do I need an NDA before the demo?", a: "An NDA is available upon request and provided same-day. While not required for the initial demo, many clients prefer to have one in place before discussing specific challenges or strategic objectives." },
    { q: "Can I customize what I see in the demo?", a: "Yes. Before your demo, our team conducts a pre-demo consultation to understand your industry, use case, and strategic objectives. The demo is then tailored to showcase the capabilities most relevant to your needs." },
    { q: "Is the demo free?", a: "The demo is provided at no cost to qualified organizations. It is designed to demonstrate platform capabilities and help you make an informed decision about Perception X2." },
    { q: "What happens after the demo?", a: "After the demo, our team answers your questions, discusses how Perception X2 applies to your organization, and provides a customized proposal outlining recommended deployment scope, tier, and timeline. There is no obligation." },
    { q: "Can I bring my team to the demo?", a: "Yes. We recommend including all relevant stakeholders — C-suite executives, communications directors, technology leaders, and anyone who will be involved in the decision-making process. Additional attendees can be added to the session." },
    { q: "What industries is the demo relevant for?", a: "Perception X2 serves organizations across government, enterprise, political campaigns, defense, financial services, healthcare, technology, and public figure management. Any sector where perception directly impacts outcomes benefits from the platform. Your demo will be customized to your industry." },
    { q: "How do I prepare for the demo?", a: "No preparation is required. However, if you have specific challenges, competitive concerns, or strategic objectives you would like addressed, share them during the pre-demo consultation. This helps us tailor the demonstration to your exact needs." },
    { q: "How quickly can we see results after deployment?", a: "Initial impact is typically measurable within 2-4 weeks, with significant results within 60-90 days. Crisis response scenarios show immediate impact. Results compound over time as AI systems learn and optimize." },
    { q: "Does Perception X2 integrate with existing tools?", a: "Yes. Perception X2 offers RESTful API access, native CRM integrations, marketing platform connections, and analytics ecosystem compatibility. Our engineering team also develops custom integrations for enterprise clients." },
  ],
};

export interface PaaData {
  eyebrow: string;
  title: string;
  items: { q: string; a: string }[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const paa: PaaData = {
  eyebrow: "People Also Ask",
  title: "Perception X2 Demo — What People Ask",
  items: [
    { q: "How do I request a demo of Perception X2?", a: "Visit perception.ac/demo and complete the contact form with your organization details. Our team responds within 24 hours to schedule your personalized guided platform demonstration." },
    { q: "What does the Perception X2 platform look like?", a: "Perception X2 is a comprehensive dashboard-driven platform with real-time analytics, narrative engineering tools, amplification orchestration, and crisis response interfaces. A live demo shows the actual platform — not mockups or slides." },
    { q: "How long does a Perception X2 demo take?", a: "A standard demo runs 45-60 minutes and covers all core platform capabilities. Extended sessions with deep-dive modules on specific features — such as narrative engineering or crisis response — are available upon request." },
    { q: "Can I see Perception X2 before committing?", a: "Yes. The demo is provided at no cost to qualified organizations. It demonstrates the live platform in the context of your specific industry and objectives, giving you a clear understanding before any commitment." },
    { q: "What is shown in the Perception X2 demo?", a: "The demo covers narrative engineering, AI content intelligence, multi-platform amplification, 144-dimensional emotional resonance analytics, crisis response systems, and real-time performance dashboards — all demonstrated in the live platform environment." },
    { q: "How is Perception X2 different from other reputation management platforms?", a: "Perception X2 operates on nine proprietary AI engines that deliver autonomous perception amplification — not monitoring or reporting. The platform achieves 300-500% narrative penetration improvement with 95-98% organic authenticity, capabilities unmatched by conventional tools." },
    { q: "What results does Perception X2 deliver?", a: "Perception X2 delivers 300-500% improvement in narrative penetration, 95-98% organic authenticity rating, sub-hour campaign activation, and real-time crisis response. These outcomes are documented, repeatable, and verified across 300+ engagements." },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  body: string;
  cta: { label: string; href: string };
  trustBar: string[];
  pillars?: { title: string; body: string }[];
}

export const demoCta: CtaData = {
  eyebrow: "Get Started",
  title: "Ready to See Perception X2 in Action?",
  intro: "Your perception is being shaped right now — by you or by others. Perception X2 gives you the autonomous, AI-powered platform to command that narrative with precision and scale.",
  body: "Request a guided demo and experience the platform that delivers 300-500% narrative penetration improvement with 95-98% organic authenticity. No slides. No abstractions. Live platform, live results.",
  cta: { label: "Request a Live Demo", href: "#demo-form" },
  trustBar: ["15+ Years", "300+ Elite Clients", "Zero Security Incidents", "99.9999% Uptime"],
};
